import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ResponsiveImage from 'components/ResponsiveImage';
import BackgroundVideo from 'components/BackgroundVideo';
import Icon from 'components/Icon';
import LinkButton from 'components/LinkButton';
import { breakpoints } from 'js/breakpoints';
import {
  getFullWidthImageSourcesMobile,
  getFullWidthImageSourcesDesktop,
} from 'js/image-sources';
import Link from '../Link';

import * as css from './Banner.module.scss';

const Banner = ({ tagline, intro, image, backgroundVideo, button, lang }) => {
  const bannerContainerRef = useRef();
  const [videoPaused, setVideoPaused] = useState(false);

  const toggleBackgroundVideoState = () => {
    setVideoPaused(prev => !prev);
  };

  return (
    <div
      className={cn(css.banner, {
        [css.bannerWithText]: tagline || button || intro,
        [css.bannerWithVideoWithoutLink]: backgroundVideo && !button,
      })}
      ref={bannerContainerRef}
    >
      {backgroundVideo ? (
        <BackgroundVideo {...backgroundVideo} pause={videoPaused} />
      ) : image ? (
        <ResponsiveImage
          {...image}
          fluid
          fetchPriority="high"
          className={css.bannerImage}
          sources={[
            ...getFullWidthImageSourcesMobile(300, 960),
            ...getFullWidthImageSourcesDesktop(448, 960, 1024),
            {
              width: 1440,
              height: 448,
              media: breakpoints.xl,
            },
          ]}
        />
      ) : null}
      {(tagline || button || intro) && (
        <div className={css.bannerContainer}>
          {tagline && <h1 className={css.tagline}>{tagline}</h1>}
          {intro && (
            <div
              className={cn(css.intro, 'wysiwyg')}
              dangerouslySetInnerHTML={{ __html: intro }}
            />
          )}
          {button && (
            <LinkButton {...button} className={css.bannerContainerBtn} />
          )}
        </div>
      )}
      {backgroundVideo && (
        <React.Fragment>
          {lang.playVideoAriaDescriptionLabel &&
            lang.pauseVideoAriaDescriptionLabel && (
              <div id="video-description" className={css.hidden}>
                {videoPaused
                  ? lang.playVideoAriaDescriptionLabel
                  : lang.pauseVideoAriaDescriptionLabel}
              </div>
            )}
          <button
            className={css.bannerPlayBtn}
            {...(lang.playVideoAriaLabel &&
              lang.pauseVideoAriaLabel && {
                'aria-label': videoPaused
                  ? lang.playVideoAriaLabel
                  : lang.pauseVideoAriaLabel,
              })}
            {...(lang.playVideoAriaDescriptionLabel &&
              lang.pauseVideoAriaDescriptionLabel && {
                'aria-describedby': 'video-description',
              })}
            onClick={toggleBackgroundVideoState}
          >
            <Icon
              use={videoPaused ? 'play' : 'pause'}
              className={css.bannerPlayBtnIcon}
            />
          </button>
        </React.Fragment>
      )}
    </div>
  );
};

Banner.propTypes = {
  tagline: PropTypes.string,
  intro: PropTypes.string,
  image: PropTypes.exact(ResponsiveImage.propTypes),
  backgroundVideo: PropTypes.exact(BackgroundVideo.propTypes),
  button: PropTypes.exact(Link.propTypes),
  lang: PropTypes.shape({
    playVideoAriaLabel: PropTypes.string,
    pauseVideoAriaLabel: PropTypes.string,
    playVideoAriaDescriptionLabel: PropTypes.string,
    pauseVideoAriaDescriptionLabel: PropTypes.string,
  }),
};

export default Banner;
